<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import AdminHeaderTabs from '@/screens/_components/admin/HeaderTabs.vue'
import { usePermissions, PERMISSIONS } from '@/composables/usePermissions'
import { useBillingStore } from '@/stores/billing'
import BillingStatusAlert from '@/screens/trusted/admin/billing/BillingStatusAlert.vue'
import PlanCard from '@/screens/trusted/admin/billing/PlanCard.vue'
import UpcomingInvoiceCard from '@/screens/trusted/admin/billing/UpcomingInvoiceCard.vue'
import PaymentDetailsCard from '@/screens/trusted/admin/billing/PaymentDetailsCard.vue'
import BillingHistory from '@/screens/trusted/admin/billing/BillingHistory.vue'
import PaymentMethodSlideIn from '@/screens/trusted/admin/billing/PaymentMethodSlideIn.vue'
import BillingDetailsSlideIn from '@/screens/trusted/admin/billing/BillingDetailsSlideIn.vue'
import DiscountCodeSlideIn from '@/screens/trusted/admin/billing/DiscountCodeSlideIn.vue'

/**
 * The billing screen at /admin/billing.
 *
 * It only lays out the cards, which read their data from the billing store.
 * People without billing permission to change things see the same page without
 * the buttons.
 */
const route = useRoute()
const billingStore = useBillingStore()
const { can } = usePermissions()

const canRead = computed(() => can(PERMISSIONS.BILLING_READ))
const canManage = computed(() => can(PERMISSIONS.BILLING_UPDATE))

const paymentMethodOpen = ref(false)
const detailsOpen = ref(false)
const discountOpen = ref(false)

const summary = computed(() => billingStore.summary)
const loadingSummary = computed(
    () => billingStore.loading.summary && !summary.value
)
const summaryError = computed(() =>
    summary.value ? null : billingStore.errors.summary
)

function openPaymentMethod() {
    if (canManage.value) paymentMethodOpen.value = true
}

// Links that mean "update the card" (the app banner's button) arrive with
// ?openPaymentMethod=1. Drop it from the address bar through the History API
// rather than the router: the trusted layout keys <router-view> on the full
// route, so a router-driven query change would remount this screen and close
// the slide-in we just opened. This still stops it opening again on refresh.
watch(
    () => route.query.openPaymentMethod,
    (requested) => {
        if (!requested) return

        openPaymentMethod()

        const url = new window.URL(window.location.href)
        url.searchParams.delete('openPaymentMethod')
        window.history.replaceState(
            window.history.state,
            '',
            url.pathname + url.search + url.hash
        )
    },
    { immediate: true }
)

onMounted(() => {
    if (canRead.value) {
        billingStore.refreshAll()
    }
})
</script>

<template>
    <main
        data-testid="new-billing-screen"
        class="min-w-0 flex-1 border-t border-taupe-200 lg:flex overflow-y-auto bg-taupe-50"
    >
        <section
            aria-labelledby="primary-heading"
            class="flex h-full min-w-0 flex-1 flex-col lg:order-last pt-6 sm:pt-8 lg:pt-10 xl:max-w-7xl xl:mx-auto w-full"
        >
            <h1 id="primary-heading" class="sr-only">Admin: Billing</h1>
            <div class="px-4 sm:px-6 lg:px-8 pb-12 space-y-6">
                <div class="sm:flex sm:items-center sm:justify-between">
                    <div class="sm:flex-auto">
                        <admin-header-tabs current="billing" />
                    </div>
                </div>

                <zen-denied-state
                    v-if="!canRead"
                    data-testid="billing-denied"
                    title="Billing is for owners and admins"
                    message="Ask an owner or admin on your account if you need to see billing."
                />

                <template v-else>
                    <p
                        v-if="!canManage"
                        data-testid="billing-read-only"
                        class="rounded-lg bg-taupe-100 p-3 text-sm text-taupe-700"
                    >
                        You can see billing here but not change it. Ask an owner
                        or admin to make changes.
                    </p>

                    <billing-status-alert
                        @update-payment-method="openPaymentMethod"
                    />

                    <div
                        v-if="summaryError"
                        data-testid="billing-error"
                        class="space-y-3 rounded-xl border border-red-200 bg-red-50 p-5"
                    >
                        <p class="text-sm text-red-800">
                            We couldn't load your billing. {{ summaryError }}
                        </p>
                        <zen-button
                            variant="secondary"
                            size="small"
                            data-testid="billing-retry"
                            @click="billingStore.refreshAll()"
                        >
                            Try again
                        </zen-button>
                    </div>

                    <div
                        v-else-if="loadingSummary"
                        class="grid gap-6 lg:grid-cols-2"
                        data-testid="billing-loading"
                    >
                        <zen-skeleton class="h-64 w-full" />
                        <zen-skeleton class="h-64 w-full" />
                    </div>

                    <template v-else-if="summary">
                        <div class="grid gap-6 lg:grid-cols-2">
                            <plan-card />
                            <upcoming-invoice-card
                                @update-payment-method="openPaymentMethod"
                            />
                        </div>

                        <payment-details-card
                            @edit-payment-method="paymentMethodOpen = true"
                            @edit-details="detailsOpen = true"
                            @edit-discount="discountOpen = true"
                        />

                        <billing-history />
                    </template>
                </template>
            </div>
        </section>

        <payment-method-slide-in
            :is-open="paymentMethodOpen"
            @close="paymentMethodOpen = false"
        />
        <billing-details-slide-in
            :is-open="detailsOpen"
            @close="detailsOpen = false"
        />
        <discount-code-slide-in
            :is-open="discountOpen"
            @close="discountOpen = false"
        />
    </main>
</template>
